import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Package, CheckCircle2, XCircle, Tags, Factory, DownloadCloud, RefreshCw, ArrowRight, ClipboardList, FileText } from "lucide-react";
import client from "@/lib/api";

const Stat = ({ icon: Icon, label, value, tone = "graphite", testid }) => (
  <div data-testid={testid} className="bg-white border border-border rounded-lg p-5">
    <div className="flex items-center justify-between">
      <span className="mono text-xs uppercase tracking-[0.15em] text-steel-light">{label}</span>
      <Icon className={`w-4 h-4 ${tone === "orange" ? "text-orange" : tone === "green" ? "text-emerald-500" : "text-steel-light"}`} />
    </div>
    <div className="mono text-3xl font-bold text-graphite mt-3">{value ?? "—"}</div>
  </div>
);

const LogRow = ({ log }) => {
  if (!log) return <p className="text-sm text-steel-light">No runs yet.</p>;
  return (
    <div className="flex items-center justify-between text-sm">
      <div className="flex items-center gap-2">
        <span className={`w-2 h-2 rounded-full ${log.status === "completed" ? "bg-emerald-500" : log.status === "running" ? "bg-amber-500" : "bg-red-500"}`} />
        <span className="capitalize text-graphite">{log.status}</span>
      </div>
      <span className="mono text-xs text-steel-light">{log.added}+ / {log.updated}↻ · {log.started_at ? new Date(log.started_at).toLocaleString() : ""}</span>
    </div>
  );
};

const Dashboard = () => {
  const [s, setS] = useState(null);
  useEffect(() => { client.get("/admin/stats").then((r) => setS(r.data)); }, []);

  return (
    <div>
      <h1 className="text-2xl font-bold tracking-tight text-graphite">Dashboard</h1>
      <p className="text-sm text-steel-light mt-1 mb-6">Overview of the imported catalogue.</p>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat icon={Package} label="Products" value={s?.products} testid="stat-products" />
        <Stat icon={CheckCircle2} label="In Stock" value={s?.in_stock} tone="green" testid="stat-instock" />
        <Stat icon={XCircle} label="Out of Stock" value={s?.out_of_stock} testid="stat-outofstock" />
        <Stat icon={Tags} label="Categories" value={s?.categories} testid="stat-categories" />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
        <Link to="/admin/orders" className="block"><Stat icon={ClipboardList} label="Orders" value={s?.orders} tone="orange" testid="stat-orders" /></Link>
        <Link to="/admin/orders" className="block"><Stat icon={FileText} label="Quotes" value={s?.quotes} testid="stat-quotes" /></Link>
        <Link to="/admin/orders" className="block"><Stat icon={CheckCircle2} label="Pending" value={s?.pending_orders} tone="green" testid="stat-pending" /></Link>
        <Stat icon={DownloadCloud} label="Priced Products" value={s?.priced_products} testid="stat-priced" />
      </div>

      <div className="grid lg:grid-cols-3 gap-4 mt-4">
        <Stat icon={Factory} label="Brands" value={s?.brands} testid="stat-brands" />
        <Stat icon={Package} label="Public Prices" value={s?.public_prices} testid="stat-prices" />
        <Link to="/admin/settings" className="bg-graphite text-white rounded-lg p-5 flex flex-col justify-between hover:bg-graphite/90 transition-colors">
          <div className="flex items-center justify-between"><span className="mono text-xs uppercase tracking-[0.15em] text-white/50">Action</span><RefreshCw className="w-4 h-4 text-orange" /></div>
          <div className="flex items-center gap-2 mt-3 font-semibold">Sync Catalogue <ArrowRight className="w-4 h-4" /></div>
        </Link>
      </div>

      <div className="grid lg:grid-cols-2 gap-4 mt-6">
        <div className="bg-white border border-border rounded-lg p-5">
          <div className="flex items-center gap-2 mb-3"><DownloadCloud className="w-4 h-4 text-orange" /><span className="font-semibold text-graphite">Last Import</span></div>
          <LogRow log={s?.last_import} />
        </div>
        <div className="bg-white border border-border rounded-lg p-5">
          <div className="flex items-center gap-2 mb-3"><RefreshCw className="w-4 h-4 text-orange" /><span className="font-semibold text-graphite">Last Sync</span></div>
          <LogRow log={s?.last_sync} />
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
